import { useEffect, useState } from 'react';
import {
  createProject,
  deleteProject,
  listTags,
  updateProject,
  type Project,
} from '../../lib/projects';
import { PROJECT_STATUSES, STATUS_LABELS, type ProjectStatus } from '../../lib/projectStatus';
import { ImageUpload } from './ImageUpload';
import { TagInput } from './TagInput';
import { Alert, Button, Field, Input, Select, Textarea } from '../ui';
import { useToast } from '../toast';

type FormState = {
  name: string;
  description: string;
  tags: string[];
  status: ProjectStatus;
  pinned: boolean;
};

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  tags: [],
  status: 'IDEA',
  pinned: false,
};

type ProjectFormProps = {
  /** Absent = create mode: nothing to update, and no delete button. */
  project?: Project;
  onSaved: (project: Project) => void;
  onCancel: () => void;
  onDeleted?: () => void;
};

/**
 * Shared by the /projects/new page and the detail page's Edit tab. It never
 * loads the project itself -- in edit mode the detail shell has already fetched
 * it, and in create mode there is nothing to fetch.
 */
export function ProjectForm({ project, onSaved, onCancel, onDeleted }: ProjectFormProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(() =>
    project
      ? {
          name: project.name,
          description: project.description ?? '',
          tags: project.tags,
          status: project.status,
          pinned: project.pinned,
        }
      : EMPTY_FORM,
  );
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(project?.imageUrl ?? null);
  /** undefined = unchanged, null = cleared, string = a new pending upload. */
  const [imageKey, setImageKey] = useState<string | null | undefined>(undefined);

  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    listTags(controller.signal)
      .then((data) => setSuggestions(data.tags))
      // Suggestions are a convenience; failing to load them is not worth surfacing.
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    const name = form.name.trim();
    if (!name) {
      setNameError('Give the project a name.');
      return;
    }
    setNameError(null);
    setError(null);
    setSaving(true);

    const payload = {
      name,
      description: form.description.trim() || null,
      tags: form.tags,
      status: form.status,
      pinned: form.pinned,
      // Omitted entirely when untouched, so the server leaves it alone.
      ...(imageKey === undefined ? {} : { imageKey }),
    };

    try {
      const saved = project
        ? await updateProject(project.id, payload)
        : await createProject(payload);
      // The upload is no longer pending once the server has promoted it.
      setImageKey(undefined);
      setPreviewUrl(saved.project.imageUrl);
      setSaving(false);
      // Both callers navigate away on save, so the form itself is gone by the
      // time the request lands -- this is the only thing that says it worked.
      toast(project ? `Saved ${saved.project.name}` : `Created ${saved.project.name}`);
      onSaved(saved.project);
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save this project.');
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!project) return;
    setSaving(true);
    try {
      await deleteProject(project.id);
      toast(`Deleted ${project.name}`);
      onDeleted?.();
    } catch (deleteError: unknown) {
      setError(deleteError instanceof Error ? deleteError.message : 'Could not delete this project.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-5">
      {error ? <Alert>{error}</Alert> : null}

      <Field label="Name" htmlFor="name" error={nameError}>
        <Input
          id="name"
          value={form.name}
          maxLength={120}
          autoFocus
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
      </Field>

      <Field label="Description" htmlFor="description">
        <Textarea
          id="description"
          rows={4}
          maxLength={4000}
          value={form.description}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
        />
      </Field>

      <Field label="Status" htmlFor="status">
        <Select
          id="status"
          value={form.status}
          onChange={(event) => setForm({ ...form, status: event.target.value as ProjectStatus })}
        >
          {PROJECT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </Select>
      </Field>

      {/* Not a <Field>: that renders the label above the control, which reads
          wrong for a checkbox. The hint below copies Field's own styling. */}
      <div>
        <label
          htmlFor="pinned"
          className="flex items-center gap-2 text-sm font-medium text-stone-700"
        >
          <input
            id="pinned"
            type="checkbox"
            checked={form.pinned}
            onChange={(event) => setForm({ ...form, pinned: event.target.checked })}
            // `form-checkbox` opts this into @tailwindcss/forms. Without the
            // plugin the three classes after it styled nothing at all, and this
            // rendered as raw platform chrome -- an OS-blue checkbox sitting in
            // the middle of a honey UI.
            className="form-checkbox h-4 w-4 rounded border-stone-300 text-honey-600 focus:ring-honey-500 focus:ring-offset-0"
          />
          Pinned
        </label>
        <p className="mt-1 text-sm text-stone-500">
          Pinned projects sort first and show on the dashboard.
        </p>
      </div>

      <Field label="Tags" htmlFor="tags" hint="Lowercased and de-duplicated when saved.">
        <TagInput
          tags={form.tags}
          suggestions={suggestions}
          onChange={(tags) => setForm({ ...form, tags })}
        />
      </Field>

      <Field label="Cover image" htmlFor="image">
        <ImageUpload
          previewUrl={previewUrl}
          onUploaded={(key, localPreviewUrl) => {
            setImageKey(key);
            setPreviewUrl(localPreviewUrl);
          }}
          onCleared={() => {
            setImageKey(null);
            setPreviewUrl(null);
          }}
        />
      </Field>

      <div className="flex items-center justify-between border-t border-stone-200 pt-5">
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving…' : project ? 'Save changes' : 'Create project'}
          </Button>
          <Button variant="ghost" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
        </div>

        {/* Inline rather than window.confirm, which blocks the page. */}
        {project ? (
          confirmingDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600">Delete this project?</span>
              <Button variant="danger" disabled={saving} onClick={() => void handleDelete()}>
                Delete
              </Button>
              <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                Keep
              </Button>
            </div>
          ) : (
            <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
              Delete
            </Button>
          )
        ) : null}
      </div>
    </form>
  );
}
